import { getPersistedValue, setPersistedValue } from './utils';

const DEFAULT_BUCKETS = ['A', 'B'];
const DEFAULT_BUCKET = 'A';

const normalizeBucket = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');

const normalizeExperimentId = (value) => {
    const fallbackId = 'default';
    if (typeof value !== 'string') return fallbackId;
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return fallbackId;
    return trimmed.replace(/[^a-z0-9_-]/g, '_');
};

const isValidBucket = (bucket, buckets = DEFAULT_BUCKETS) => buckets.includes(bucket);

export const getExperimentKeys = (experimentId) => {
    const safeId = normalizeExperimentId(experimentId);
    return {
        bucket: `cr_exp_${safeId}_bucket`,
        enrolled: `cr_exp_${safeId}_enrolled`,
    };
};

export const clearPersistedExperiment = ({ experimentId, legacyKeys = null, additionalKeys = [] }) => {
    const keys = getExperimentKeys(experimentId);
    [keys.bucket, keys.enrolled, legacyKeys?.bucket, legacyKeys?.enrolled, ...additionalKeys]
        .filter(Boolean)
        .forEach((key) => setPersistedValue(key, key, null));
};

const saveValue = (key, value) => {
    setPersistedValue(key, key, value);
};

const readValue = (key) => getPersistedValue(key, key);

const resolveBucketFromRandomValue = ({
    buckets,
    defaultBucket,
    bucketWeights = null,
}) => {
    const normalizedWeights = Array.isArray(buckets)
        ? buckets.map((bucket) => ({
            bucket,
            weight: Number(bucketWeights?.[bucket]),
        }))
        : [];
    const hasWeightedAllocation = normalizedWeights.some(({ weight }) => Number.isFinite(weight) && weight > 0);

    if (!hasWeightedAllocation) {
        const randomIndex = Math.floor(Math.random() * buckets.length);
        return buckets[randomIndex] ?? defaultBucket;
    }

    const totalWeight = normalizedWeights.reduce((sum, { weight }) => {
        return sum + (Number.isFinite(weight) && weight > 0 ? weight : 0);
    }, 0);

    if (totalWeight <= 0) {
        const randomIndex = Math.floor(Math.random() * buckets.length);
        return buckets[randomIndex] ?? defaultBucket;
    }

    const randomThreshold = Math.random() * totalWeight;
    let accumulatedWeight = 0;
    for (const { bucket, weight } of normalizedWeights) {
        const safeWeight = Number.isFinite(weight) && weight > 0 ? weight : 0;
        accumulatedWeight += safeWeight;
        if (randomThreshold < accumulatedWeight) {
            return bucket;
        }
    }

    return normalizedWeights[normalizedWeights.length - 1]?.bucket ?? defaultBucket;
};

export const persistExperimentBucket = ({
    experimentId,
    bucket,
    buckets = DEFAULT_BUCKETS,
    legacyKeys = null,
}) => {
    const normalizedBucket = normalizeBucket(bucket);
    if (!isValidBucket(normalizedBucket, buckets)) {
        return null;
    }

    const keys = getExperimentKeys(experimentId);
    saveValue(keys.bucket, normalizedBucket);
    saveValue(keys.enrolled, '1');

    if (legacyKeys?.bucket) {
        saveValue(legacyKeys.bucket, normalizedBucket);
    }
    if (legacyKeys?.enrolled) {
        saveValue(legacyKeys.enrolled, '1');
    }

    return normalizedBucket;
};

export const getPersistedExperimentBucket = ({
    experimentId,
    buckets = DEFAULT_BUCKETS,
    legacyKeys = null,
}) => {
    const keys = getExperimentKeys(experimentId);
    const candidates = [readValue(keys.bucket), legacyKeys?.bucket ? readValue(legacyKeys.bucket) : null];
    for (const candidate of candidates) {
        const normalized = normalizeBucket(candidate);
        if (isValidBucket(normalized, buckets)) {
            return normalized;
        }
    }
    return null;
};

export const resolveExperimentBucket = ({
    experimentId,
    mode = 'AB',
    userBucket,
    buckets = DEFAULT_BUCKETS,
    defaultBucket = DEFAULT_BUCKET,
    bucketWeights = null,
    legacyKeys = null,
} = {}) => {
    const normalizedMode = normalizeBucket(mode);

    if (isValidBucket(normalizedMode, buckets)) {
        return persistExperimentBucket({ experimentId, bucket: normalizedMode, buckets, legacyKeys });
    }

    if (normalizedMode === 'AB') {
        const normalizedUserBucket = normalizeBucket(userBucket);
        if (isValidBucket(normalizedUserBucket, buckets)) {
            return persistExperimentBucket({ experimentId, bucket: normalizedUserBucket, buckets, legacyKeys });
        }

        const persisted = getPersistedExperimentBucket({ experimentId, buckets, legacyKeys });
        if (persisted) {
            return persistExperimentBucket({ experimentId, bucket: persisted, buckets, legacyKeys });
        }

        const randomBucket = resolveBucketFromRandomValue({
            buckets,
            defaultBucket,
            bucketWeights,
        });
        return persistExperimentBucket({ experimentId, bucket: randomBucket, buckets, legacyKeys });
    }

    return persistExperimentBucket({ experimentId, bucket: defaultBucket, buckets, legacyKeys });
};
